import { useMemo, useState } from 'react';
import { BadgeCheck, Paperclip, Search } from 'lucide-react';
import moment from 'moment';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useOwnershipClaimsQuery } from '@/client/api/ownership-claims/ownership-claims.query';
import type { ClaimStatusFilter, OwnershipClaim } from '@/client/types/ownership-claims';
import { useListFilters } from '@/client/hooks/useListFilters';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import Pagination from '@/client/components/common/Pagination';
import ClaimReviewDrawer from '@/client/components/admin/OwnershipClaim/ClaimReviewDrawer';
import { CLAIM_STATUS_BADGE, personLabel } from '@/client/components/admin/OwnershipClaim/claimStatus';

const PAGE_SIZE = 25;
const TABS: { id: ClaimStatusFilter; label: string }[] = [
  { id: 'pending', label: 'Pending' },
  { id: 'approved', label: 'Approved' },
  { id: 'rejected', label: 'Rejected' },
  { id: 'removed', label: 'Removed' },
  { id: 'all', label: 'All' },
];
const HEADINGS = ['Listing', 'Claimant', 'Owner', 'Files', 'Submitted', 'Status', ''];

const OwnershipClaimsPage = () => {
  useBreadcrumbs([{ label: 'Listings' }, { label: 'Ownership claims' }]);

  const { filters, page, searchTerm, debouncedSearchTerm, handleSearch, handleFilterChange, handlePageChange } =
    useListFilters<{ status: ClaimStatusFilter }>({ initialFilters: { status: 'pending' }, initialLimit: PAGE_SIZE });

  const params = useMemo(
    () => ({ status: filters.status, q: debouncedSearchTerm || undefined, page, page_size: PAGE_SIZE }),
    [filters.status, debouncedSearchTerm, page],
  );
  const { data, isLoading, isError } = useOwnershipClaimsQuery(params);
  const [selected, setSelected] = useState<OwnershipClaim | null>(null);
  const claims = data?.claims ?? [];
  const counts = data?.counts;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto space-y-6">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900">
            <BadgeCheck className="h-6 w-6 text-blue-700" />
            Ownership claims
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Requests to take over a listing someone else posted. Owners usually decide; step in when they don't
            respond, a claim is disputed, or it's spam.
          </p>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-1 rounded-lg bg-white p-1 shadow-sm">
            {TABS.map((tab) => {
              const active = filters.status === tab.id;
              const count = tab.id !== 'all' ? counts?.[tab.id] : undefined;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => handleFilterChange('status', tab.id)}
                  className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                    active ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {tab.label}
                  {count !== undefined && <span className={`ml-1.5 text-xs ${active ? 'text-blue-100' : 'text-gray-400'}`}>{count}</span>}
                </button>
              );
            })}
          </div>
          <div className="relative w-full max-w-xs">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Listing, claimant, email or claim id"
              className="pl-9"
            />
          </div>
        </div>

        <div className="overflow-hidden rounded-lg bg-white shadow">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {HEADINGS.map((heading, index) => (
                  <th
                    key={heading || index}
                    className={`px-6 py-3 text-xs font-medium uppercase tracking-wider text-gray-500 ${heading ? 'text-left' : 'text-right'}`}
                  >
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {isLoading &&
                Array.from({ length: 5 }).map((_, row) => (
                  <tr key={row}>
                    {HEADINGS.map((__, cell) => (
                      <td key={cell} className="px-6 py-4"><Skeleton className="h-4 w-24" /></td>
                    ))}
                  </tr>
                ))}

              {!isLoading && isError && (
                <tr>
                  <td colSpan={HEADINGS.length} className="px-6 py-12 text-center text-sm text-red-600">
                    Could not load ownership claims. Try again.
                  </td>
                </tr>
              )}

              {!isLoading && !isError && claims.length === 0 && (
                <tr>
                  <td colSpan={HEADINGS.length} className="px-6 py-12 text-center text-sm text-gray-500">
                    No claims match these filters.
                  </td>
                </tr>
              )}

              {claims.map((claim) => (
                <tr key={claim.claimid} className="cursor-pointer hover:bg-gray-50" onClick={() => setSelected(claim)}>
                  <td className="px-6 py-4">
                    <span className="block text-sm font-medium text-gray-900">{claim.entity?.name || `Listing #${claim.eid}`}</span>
                    <span className="block text-xs text-gray-500">claim #{claim.claimid} · eid {claim.eid}</span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700">{personLabel(claim.claimer)}</td>
                  <td className="px-6 py-4 text-sm text-gray-700">{personLabel(claim.owner)}</td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600">
                    <span className="inline-flex items-center gap-1">
                      <Paperclip className="h-3.5 w-3.5 text-gray-400" />
                      {claim.attachments?.length ?? 0}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                    {claim.date_added ? moment(claim.date_added).format('YYYY-MM-DD HH:mm') : '—'}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${CLAIM_STATUS_BADGE[claim.status]}`}>
                      {claim.status}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelected(claim);
                      }}
                    >
                      Review
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="border-t border-gray-200 px-4 py-3 sm:px-6">
            <Pagination total={data?.total ?? 0} page={page} limit={PAGE_SIZE} setPage={handlePageChange} showButtons />
          </div>
        </div>
      </div>

      {selected && <ClaimReviewDrawer summary={selected} onClose={() => setSelected(null)} />}
    </div>
  );
};

export default OwnershipClaimsPage;
